import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { postApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, CheckCircle2, XCircle, Loader2, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#1D314D";
const GOLD = "#CEAB65";

interface CIMApprovalBannerProps {
  dealId: string;
  businessName?: string;
  onApproved?: () => void;
  onChangesRequested?: () => void;
  // AJOUTÉ — vue broker : statut + "Voir le CIM" + "Demander des
  // modifications" restent disponibles (sellerReject() autorise toujours
  // le broker), mais le bouton "Approuver" (paiement 2 449 $) est retiré —
  // seul le vrai vendeur peut approuver et payer (voir
  // deal-approval.service.ts sellerApprove()).
  readOnly?: boolean;
}

const T = {
  en: {
    title: "Your CIM is ready for review",
    subtitle: "Please review your Confidential Information Memorandum before it goes live.",
    view: "View CIM",
    approve: "Approve",
    request: "Request Changes",
    approving: "Approving...",
    sending: "Sending...",
    redirecting: "Redirecting to payment...",
    notesLabel: "Describe the changes you would like:",
    notesPlaceholder: "Explain what should be revised...",
    submit: "Submit request",
    cancel: "Cancel",
    approved: "CIM approved — redirecting to payment.",
    requested: "Change request sent.",
    notesRequired: "Please provide details.",
    error: "An error occurred.",
    paymentError: "CIM approved, but could not start payment. Please contact support.",
    awaitingSellerApproval: "Awaiting seller approval",
  },
  fr: {
    title: "Votre Mémorandum d'Information est prêt pour révision",
    subtitle: "Veuillez vérifier votre CIM avant publication.",
    view: "Voir le CIM",
    approve: "Approuver",
    request: "Demander des modifications",
    approving: "Approbation...",
    sending: "Envoi...",
    redirecting: "Redirection vers le paiement...",
    notesLabel: "Décrivez les modifications souhaitées :",
    notesPlaceholder: "Expliquez ce qui doit être révisé...",
    submit: "Envoyer la demande",
    cancel: "Annuler",
    approved: "CIM approuvé — redirection vers le paiement.",
    requested: "Demande de modification envoyée.",
    notesRequired: "Veuillez fournir des détails.",
    error: "Une erreur est survenue.",
    paymentError: "CIM approuvé, mais le paiement n'a pas pu démarrer. Contactez le support.",
    awaitingSellerApproval: "En attente d'approbation du vendeur",
  },
};

export default function CIMApprovalBanner({
  dealId,
  businessName,
  onApproved,
  onChangesRequested,
  readOnly = false,
}: CIMApprovalBannerProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = T[(language as "en" | "fr") || "en"];

  const [approving, setApproving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const isSubmittingRef = useRef(false);
  const { user } = useAuth();

  const handleApprove = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setApproving(true);
    try {
      // 1. Approuver le CIM — retourne { requiresPayment, paymentAmount, paymentDescription }
      const approval = await postApi(`/deals/${dealId}/seller-approve`, {});

      // Le deal a déjà été payé (rechargement/re-visite de la bannière
      // après un paiement déjà complété) — pas de nouvelle session
      // Stripe, juste informer et fermer la bannière normalement.
      if (approval?.alreadyPaid) {
        toast({ description: t.approved });
        onApproved?.();
        return;
      }

      if (!approval?.requiresPayment) {
        toast({ description: t.approved });
        onApproved?.();
        return;
      }

           // 2. Créer la vraie session Stripe (2 449 $, plan "deal_acceptance")
      const origin = window.location.origin;
      // AJOUTÉ (16 sept 2026) — transmet userRole dans les metadata,
      // comme DealCheckout.tsx le fait déjà pour la pré-évaluation.
      // Sans ce champ, payments.service.ts createCheckoutSession()
      // (côté NestJS) et getCheckoutSessionInfo.ts (côté Firebase
      // Functions, affiché sur PaymentSuccess.tsx) n'ont aucun moyen de
      // distinguer un vendeur payant lui-même d'un broker payant pour
      // son compte — les deux retombent par défaut sur "seller",
      // correct pour le cas normal (vendeur paie), mais incorrect si un
      // broker règle ces frais en direct pour son vendeur représenté
      // (cas légal encore incertain à ce jour dans ce projet — voir
      // note ouverte sur la permission de payer au nom du vendeur).
      //
      // resolveUserRole reprend le même pattern que DealCheckout.tsx :
      // priorité à user.roles[] (le compte réel connecté) plutôt qu'à
      // une valeur supposée, pour éviter le même défaut de résolution
      // déjà corrigé ailleurs dans ce projet (DealCheckout.tsx,
      // PaymentSuccess.tsx).
      const accountRoles: string[] = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
      const resolvedUserRole = accountRoles.includes('broker') ? 'broker'
        : accountRoles.includes('seller') ? 'seller'
        : 'seller'; // repli sur seller — cas normal attendu pour ce paiement précis
 
      const checkout = await postApi("/payments/checkout", {
        type: "one_time",
        planId: "deal_acceptance",
        dealId,
        description: approval.paymentDescription || "AcquizGroup — Deal Publication Fee",
        successUrl: `${origin}/payment-success?type=publication&dealId=${dealId}`,
        cancelUrl: `${origin}/seller/dashboard?canceled=true`,
        metadata: { userRole: resolvedUserRole },
      });

      if (!checkout?.url) {
        throw new Error("No checkout URL returned");
      }

      toast({ description: t.redirecting });
      window.location.href = checkout.url;
    } catch (e: any) {
      toast({ title: t.error, description: e?.message || t.paymentError, variant: "destructive" });
    } finally {
      setApproving(false);
      isSubmittingRef.current = false;
    }
  };

  const handleRequestChanges = async () => {
    if (!notes.trim()) {
      toast({ description: t.notesRequired, variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await postApi(`/deals/${dealId}/seller-reject`, { feedback: notes.trim() });
      toast({ description: t.requested });
      setShowNotes(false);
      setNotes("");
      onChangesRequested?.();
    } catch (e: any) {
      toast({ title: t.error, description: e?.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-lg border-l-4 p-5 shadow-sm"
      style={{ background: NAVY, borderLeftColor: GOLD, color: "white" }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <FileText className="h-6 w-6 mt-0.5" style={{ color: GOLD }} />
          <div>
            <h3 className="font-semibold text-lg">{t.title}</h3>
            <p className="text-sm opacity-80">
              {businessName ? `${businessName} — ` : ""}{t.subtitle}
            </p>
          </div>
        </div>

        {!showNotes && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {readOnly && (
              <span
                className="text-sm px-3 py-1.5 rounded-md font-medium"
                style={{ background: "rgba(206,171,101,0.15)", color: GOLD }}
              >
                {t.awaitingSellerApproval}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/cim-preview/${dealId}`)}
              className="bg-transparent border-white/40 text-white hover:bg-white/10"
            >
              <Eye className="h-4 w-4 mr-1" />
              {t.view}
            </Button>
            {/* Approuver (déclenche le paiement de 2 449 $) — réservé au
                vrai vendeur. Retiré en lecture seule (vue broker) car
                deal-approval.service.ts sellerApprove() le rejetterait de
                toute façon ; on évite d'afficher un bouton qui échouerait
                systématiquement. */}
            {!readOnly && (
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={approving}
                style={{ background: GOLD, color: NAVY }}
                className="font-semibold hover:opacity-90"
              >
                {approving ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{t.approving}</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-1" />{t.approve}</>
                )}
              </Button>
            )}
            {/* Demander des modifications — reste disponible en lecture
                seule : sellerReject() autorise toujours le broker
                (deal.brokerId === userId), donc un broker doit pouvoir
                relayer une demande de révision sans que le vendeur ait à
                s'en charger lui-même. Seule l'approbation/paiement est
                restreinte au vendeur. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNotes(true)}
              className="bg-transparent border-white/40 text-white hover:bg-white/10"
            >
              <XCircle className="h-4 w-4 mr-1" />
              {t.request}
            </Button>
          </div>
        )}
      </div>

      {showNotes && (
        <div className="mt-4 space-y-3">
          <label className="block text-sm font-medium">{t.notesLabel}</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t.notesPlaceholder}
            rows={4}
            className="bg-white/95 text-foreground"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleRequestChanges}
              disabled={sending}
              style={{ background: GOLD, color: NAVY }}
              className="font-semibold hover:opacity-90"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{t.sending}</>
              ) : (
                t.submit
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowNotes(false); setNotes(""); }}
              disabled={sending}
              className="bg-transparent border-white/40 text-white hover:bg-white/10"
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}